import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretDown, CaretRight, MagnifyingGlass } from '@phosphor-icons/react';

import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import type { Location } from '@/api/locations';
import { ACTIVITY_TYPES, type ActivityType } from '@/api/activities';
import { resolve as resolveTeamColor } from '@/lib/teamColor';
import { typeTone } from '@/lib/activityType';
import { useAsyncTeamSearch, type TeamLite } from '@/hooks/useAsyncTeamSearch';

interface Props {
  slug: string;
  locations: Location[];
  selectedLocationIds: Set<string>;
  selectedTeamIds: Set<string>;
  selectedZoneIds: Set<string>;
  selectedActivityTypes: Set<ActivityType>;
  // Hides the locations+zones section entirely. The resources view
  // always renders every location as a row, so filtering them would
  // hide the very thing the view is meant to show.
  hideLocations?: boolean;
  onToggleLocation: (id: string) => void;
  onToggleTeam: (id: string) => void;
  onToggleZone: (id: string) => void;
  onToggleActivityType: (kind: ActivityType) => void;
  // Bulk-select callbacks for each section. Scoped to the currently
  // visible rows (i.e. the post-search subset) — selecting "all" when
  // a search is active means "all matching", not "everything in the
  // org". For teams the visible set is bounded by the server page
  // (top 200 of the current search).
  onSelectLocations: (ids: string[]) => void;
  onClearLocations: () => void;
  onSelectTeams: (ids: string[]) => void;
  onClearTeams: () => void;
  onClearAll: () => void;
}

interface SectionProps {
  title: string;
  open: boolean;
  onToggle: () => void;
  search: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  bulkActions?: React.ReactNode;
}

// BulkActions — paired "Select all" / "Clear" text buttons rendered on
// the right side of a section header. Mirrors the pattern from
// ApplyTemplateModal so the visual language is consistent across the
// schedule surface. Stops propagation so clicking a bulk action
// doesn't also collapse the accordion.
function BulkActions({
  selectAllLabel,
  clearLabel,
  onSelectAll,
  onClear,
  showSelectAll = true,
  showClear = true,
}: {
  selectAllLabel: string;
  clearLabel: string;
  onSelectAll: () => void;
  onClear: () => void;
  showSelectAll?: boolean;
  showClear?: boolean;
}) {
  if (!showSelectAll && !showClear) return null;
  return (
    <div className="flex shrink-0 items-center gap-1 text-[11px]">
      {showSelectAll && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelectAll();
          }}
          className="rounded-pill px-1.5 py-0.5 text-foreground/60 hover:bg-foreground/5 hover:text-foreground"
        >
          {selectAllLabel}
        </button>
      )}
      {showSelectAll && showClear && (
        <span aria-hidden className="text-foreground/30">·</span>
      )}
      {showClear && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          className="rounded-pill px-1.5 py-0.5 text-foreground/60 hover:bg-foreground/5 hover:text-foreground"
        >
          {clearLabel}
        </button>
      )}
    </div>
  );
}

// SimpleAccordionSection — like AccordionSection but without the inline
// search box. Used for fixed-size lists (e.g. the six activity types)
// where searching would be busywork.
function SimpleAccordionSection({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <section>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-1.5 py-1 text-left"
      >
        <CaretRight
          weight="bold"
          size={12}
          className={`shrink-0 text-foreground/50 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
        />
        <span className="text-xs font-semibold uppercase tracking-wider text-foreground/50">
          {title}
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <ul className="mt-0.5 flex flex-col gap-0.5">{children}</ul>
        </div>
      </div>
    </section>
  );
}

function AccordionSection({
  title,
  open,
  onToggle,
  search,
  onSearchChange,
  searchPlaceholder,
  children,
  footer,
  bulkActions,
}: SectionProps) {
  return (
    <section>
      <div className="flex w-full items-center gap-1.5 py-1">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
        >
          <CaretRight
            weight="bold"
            size={12}
            className={`shrink-0 text-foreground/50 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
          />
          <span className="min-w-0 truncate text-xs font-semibold uppercase tracking-wider text-foreground/50">
            {title}
          </span>
        </button>
        {open && bulkActions}
      </div>
      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <div className="relative mb-1 mt-0.5">
            <MagnifyingGlass
              weight="bold"
              size={11}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-foreground/40"
            />
            <input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded-md border border-border bg-transparent py-1 pl-6 pr-2 text-xs outline-none placeholder:text-foreground/30 focus:border-foreground/40"
            />
          </div>
          <ul className="flex flex-col gap-0.5">{children}</ul>
          {footer}
        </div>
      </div>
    </section>
  );
}

function TeamRow({
  team,
  checked,
  onToggle,
}: {
  team: TeamLite;
  checked: boolean;
  onToggle: () => void;
}) {
  const color = resolveTeamColor(team.name, team.color).dot;
  return (
    <li>
      <label className="flex min-w-0 cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-muted">
        <Checkbox checked={checked} onChange={onToggle} />
        <span
          className="size-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="min-w-0 flex-1 truncate text-sm">{team.name || team.id}</span>
      </label>
    </li>
  );
}

function LocationRow({
  loc,
  checked,
  onToggle,
  selectedZoneIds,
  onToggleZone,
}: {
  loc: Location;
  checked: boolean;
  onToggle: () => void;
  selectedZoneIds: Set<string>;
  onToggleZone: (id: string) => void;
}) {
  const { t } = useTranslation();
  const hasZones = loc.zones.length > 0;
  // Default-expanded only if a zone is already ticked, so the user can
  // see (and untick) it without hunting. Otherwise zones stay collapsed
  // behind the chevron — checking the location itself no longer
  // auto-opens them.
  const anyZoneSelected = loc.zones.some((z) => selectedZoneIds.has(z.id));
  const [open, setOpen] = useState(anyZoneSelected);
  const showZones = hasZones && open;
  return (
    <li>
      <div className="flex items-center gap-1 rounded-lg pr-1 hover:bg-muted">
        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 px-1.5 py-1">
          <Checkbox checked={checked} onChange={onToggle} />
          <span className="min-w-0 flex-1 truncate text-sm">{loc.name}</span>
        </label>
        {hasZones && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={
              open
                ? t('schedule.filter.collapseZones')
                : t('schedule.filter.expandZones')
            }
            className="shrink-0 rounded-md p-1 text-foreground/50 hover:bg-foreground/5 hover:text-foreground"
          >
            <CaretDown
              weight="bold"
              size={12}
              className={`transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
            />
          </button>
        )}
      </div>
      {showZones && (
        <ul className="ml-6 mt-0.5 flex flex-col gap-0.5 border-l border-border/50 pl-2">
          {loc.zones.map((z) => (
            <li key={z.id}>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-muted">
                <Checkbox
                  checked={selectedZoneIds.has(z.id)}
                  onChange={() => onToggleZone(z.id)}
                />
                <span className="truncate text-xs text-foreground/80">{z.name}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function CalendarFilterPanel({
  slug,
  locations,
  selectedLocationIds,
  selectedTeamIds,
  selectedZoneIds,
  selectedActivityTypes,
  hideLocations = false,
  onToggleLocation,
  onToggleTeam,
  onToggleZone,
  onToggleActivityType,
  onSelectLocations,
  onClearLocations,
  onSelectTeams,
  onClearTeams,
  onClearAll,
}: Props) {
  const { t } = useTranslation();
  const [locationsOpen, setLocationsOpen] = useState(true);
  const [teamsOpen, setTeamsOpen] = useState(true);
  const [typesOpen, setTypesOpen] = useState(true);
  const [locationSearch, setLocationSearch] = useState('');

  const selectedTeamIdList = useMemo(
    () => Array.from(selectedTeamIds),
    [selectedTeamIds],
  );

  // The teams section is server-side searched — top 200 plus per-ID
  // resolution for anything currently checked. Without the resolver, a
  // selected team that doesn't match the current search would still
  // count toward the filter but disappear from the list (the user
  // couldn't uncheck it without clearing the whole filter).
  const teamSearch = useAsyncTeamSearch(slug, {
    enabled: teamsOpen,
    resolveIds: selectedTeamIdList,
  });

  const hasFilter =
    selectedLocationIds.size > 0 ||
    selectedTeamIds.size > 0 ||
    selectedZoneIds.size > 0 ||
    selectedActivityTypes.size > 0;

  // Teams render in their natural (search-result) order. Selected teams
  // that aren't on the current search page are surfaced separately so
  // the user can still untick them without clearing the whole filter
  // first. Long search results are capped at VISIBLE_CAP plus a
  // "refine your search" hint — same convention as the locations section.
  const VISIBLE_CAP = 10;

  const teamRows = useMemo(() => {
    const visibleIds = new Set(teamSearch.teams.map((tm) => tm.id));
    const offSearchSelected: TeamLite[] = [];
    for (const id of selectedTeamIdList) {
      if (visibleIds.has(id)) continue;
      const t = teamSearch.resolved.get(id);
      // Resolver may not have returned yet — render a placeholder row so
      // the user can still uncheck it. Name falls back to the ID.
      offSearchSelected.push(t ?? { id, name: '', color: '' });
    }
    const restVisible = teamSearch.teams.slice(0, VISIBLE_CAP);
    // Hidden count covers two cases that look identical to the user:
    //   (a) results we fetched but capped client-side
    //   (b) results the server itself paged off
    // Combined into a single "N more — refine your search" hint.
    const hidden =
      (teamSearch.teams.length - restVisible.length) +
      (teamSearch.total - teamSearch.teams.length);
    return { offSearchSelected, rest: restVisible, hidden };
  }, [selectedTeamIdList, teamSearch.resolved, teamSearch.teams, teamSearch.total]);

  // Locations render in their natural order — no pinning. Selected
  // locations that fall outside the current search are still surfaced
  // separately so the user can untick them; otherwise typing in the
  // search box could hide a checked filter with no way to clear it.
  const locationRows = useMemo(() => {
    const needle = locationSearch.trim().toLowerCase();
    const matches = needle
      ? locations.filter((loc) => loc.name.toLowerCase().includes(needle))
      : locations;
    const matchedIds = new Set(matches.map((l) => l.id));
    const offSearchSelected = locations.filter(
      (loc) => selectedLocationIds.has(loc.id) && !matchedIds.has(loc.id),
    );
    const restAll = matches;
    const restVisible = restAll.slice(0, VISIBLE_CAP);
    const hidden = restAll.length - restVisible.length;
    return { offSearchSelected, rest: restVisible, hidden, allMatches: matches };
  }, [locations, locationSearch, selectedLocationIds]);

  // "Select all" for the locations section means "tick every location
  // currently matched by the search box" — or every location when the
  // search is empty. The button targets the full match set, not just
  // the visible 10, so the user doesn't have to scroll/refine first.
  const selectAllLocations = () => {
    onSelectLocations(locationRows.allMatches.map((l) => l.id));
  };

  // "Select all" for the teams section means "tick every team in the
  // current search page" (the server returns up to 200). When more
  // teams exist beyond the page, the existing "N more — refine your
  // search" hint is already visible to make the bound obvious.
  const selectAllTeams = () => {
    onSelectTeams(teamSearch.teams.map((tm) => tm.id));
  };

  return (
    <div className="flex flex-col gap-3">
      {!hideLocations && locations.length > 0 && (
        <AccordionSection
          title={t('schedule.filter.locations')}
          open={locationsOpen}
          onToggle={() => setLocationsOpen((v) => !v)}
          search={locationSearch}
          onSearchChange={setLocationSearch}
          searchPlaceholder={t('schedule.filter.search')}
          bulkActions={
            <BulkActions
              selectAllLabel={t('schedule.filter.selectAll')}
              clearLabel={t('schedule.filter.clearSection')}
              onSelectAll={selectAllLocations}
              onClear={onClearLocations}
              showSelectAll={locationRows.allMatches.length > 0}
              showClear={selectedLocationIds.size > 0}
            />
          }
          footer={
            locationRows.hidden > 0 ? (
              <p className="mt-1 px-1.5 text-[11px] text-foreground/50">
                {t('schedule.filter.searchMoreHint', { count: locationRows.hidden })}
              </p>
            ) : null
          }
        >
          {locationRows.rest.map((loc) => (
            <LocationRow
              key={loc.id}
              loc={loc}
              checked={selectedLocationIds.has(loc.id)}
              onToggle={() => onToggleLocation(loc.id)}
              selectedZoneIds={selectedZoneIds}
              onToggleZone={onToggleZone}
            />
          ))}
          {locationRows.offSearchSelected.length > 0 && (
            <>
              {locationRows.rest.length > 0 && (
                <li aria-hidden className="my-1 h-px bg-border/60" />
              )}
              {locationRows.offSearchSelected.map((loc) => (
                <LocationRow
                  key={`off-${loc.id}`}
                  loc={loc}
                  checked
                  onToggle={() => onToggleLocation(loc.id)}
                  selectedZoneIds={selectedZoneIds}
                  onToggleZone={onToggleZone}
                />
              ))}
            </>
          )}
          {locationRows.rest.length === 0 && locationRows.offSearchSelected.length === 0 && (
            <li className="px-1.5 py-1 text-xs text-foreground/50">
              {t('common.noResults')}
            </li>
          )}
        </AccordionSection>
      )}

      <AccordionSection
        title={t('schedule.filter.teams')}
        open={teamsOpen}
        onToggle={() => setTeamsOpen((v) => !v)}
        search={teamSearch.search}
        onSearchChange={teamSearch.setSearch}
        searchPlaceholder={t('schedule.filter.search')}
        bulkActions={
          <BulkActions
            selectAllLabel={t('schedule.filter.selectAll')}
            clearLabel={t('schedule.filter.clearSection')}
            onSelectAll={selectAllTeams}
            onClear={onClearTeams}
            showSelectAll={teamSearch.teams.length > 0}
            showClear={selectedTeamIds.size > 0}
          />
        }
        footer={
          teamRows.hidden > 0 ? (
            <p className="mt-1 px-1.5 text-[11px] text-foreground/50">
              {t('schedule.filter.searchMoreHint', { count: teamRows.hidden })}
            </p>
          ) : null
        }
      >
        {teamRows.rest.map((team) => (
          <TeamRow
            key={team.id}
            team={team}
            checked={selectedTeamIds.has(team.id)}
            onToggle={() => onToggleTeam(team.id)}
          />
        ))}
        {teamRows.offSearchSelected.length > 0 && (
          <>
            {teamRows.rest.length > 0 && (
              <li aria-hidden className="my-1 h-px bg-border/60" />
            )}
            {teamRows.offSearchSelected.map((team) => (
              <TeamRow
                key={`off-${team.id}`}
                team={team}
                checked
                onToggle={() => onToggleTeam(team.id)}
              />
            ))}
          </>
        )}
        {teamRows.rest.length === 0 && teamRows.offSearchSelected.length === 0 && !teamSearch.isLoading && (
          <li className="px-1.5 py-1 text-xs text-foreground/50">
            {t('common.noResults')}
          </li>
        )}
      </AccordionSection>

      <SimpleAccordionSection
        title={t('schedule.filter.activityTypes')}
        open={typesOpen}
        onToggle={() => setTypesOpen((v) => !v)}
      >
        {ACTIVITY_TYPES.map((kind) => {
          const tone = typeTone(kind);
          return (
            <li key={kind}>
              <label className="flex min-w-0 cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-muted">
                <Checkbox
                  checked={selectedActivityTypes.has(kind)}
                  onChange={() => onToggleActivityType(kind)}
                />
                <span
                  aria-hidden
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: tone.accent }}
                />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {t(`schedule.activity.type.option.${kind}`)}
                </span>
              </label>
            </li>
          );
        })}
      </SimpleAccordionSection>

      {hasFilter && (
        <Button variant="outline" size="sm" onClick={onClearAll} className="w-full">
          {t('schedule.filter.clearAll')}
        </Button>
      )}
    </div>
  );
}
